import { cn } from '#client/lib/cn';
import { countOf } from '#client/lib/format';

type SortDirection = 'asc' | 'desc';

export interface TableSort {
  key: string;
  direction: SortDirection;
}

type SortState = 'ascending' | 'descending' | 'none';

/**
 * A column's `aria-sort`: undefined for a column that cannot sort, `none` for
 * one that can but is not the active sort.
 */
export function ariaSortFor(
  column: { key: string; sortable?: boolean },
  sort: TableSort | undefined,
): SortState | undefined {
  if (!column.sortable) return undefined;
  if (sort?.key !== column.key) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}

/** Which arrow a sortable header shows. */
export function sortIconFor(columnKey: string, sort: TableSort | undefined): SortState {
  return ariaSortFor({ key: columnKey, sortable: true }, sort) ?? 'none';
}

/** Alignment and pinning classes shared by a column's header and body cells. */
export function cellClassName(column: { align?: string; frozen?: boolean }): string {
  return cn(
    column.align && `c-table__cell--${column.align}`,
    column.frozen && 'c-table__cell--frozen',
  );
}

/** What the live region says about the table's state. */
export function tableStatusText(loading: boolean, count: number): string {
  return loading ? 'Loading records' : `${countOf(count, 'record')} shown`;
}
